import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDateTime, hasPermission, ROLE_LABELS, USER_ACCESS_FILTER_LABELS, USER_STATUS } from '@blimo/shared';
import { Badge, Button, DataTable, ErrorState, PageHeader, SearchInput, StatusBadge } from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { useListState } from '../../lib/list-search.js';
import { UserFormSheet } from './user-form-sheet.jsx';
import { AccessExpiry } from './access-expiry.jsx';

const STATUS_OPTIONS = Object.entries(USER_STATUS).map(([value, meta]) => ({ value, label: meta.label }));
const ACCESS_OPTIONS = Object.entries(USER_ACCESS_FILTER_LABELS).map(([value, label]) => ({ value, label }));

export function UsersListPage() {
  const api = useApi();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const [createOpen, setCreateOpen] = useState(false);
  const params = { ...list.apiParams, filter: { q: search.q, status: search.status, role: search.role, access: search.access } };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.users(api, params));
  const { data: roles } = useQuery(adminQueries.roles(api));
  const roleOptions = (roles ?? []).map((r) => ({ value: r.name, label: ROLE_LABELS[r.name] ?? r.name }));
  const columns = useMemo(() => userColumns(), []);

  return (
    <>
      <PageHeader
        title="Benutzer & Zugänge"
        description="Konten für Verwaltung und Kundenportal, Rollen, Einladungen und Zwei-Faktor-Authentifizierung."
        actions={
          hasPermission(me, 'users.manage') && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden="true" /> Benutzer anlegen
            </Button>
          )
        }
      />
      <FilterBar>
        <SearchInput label="Benutzer durchsuchen" placeholder="Name oder E-Mail …" value={search.q ?? ''} onValueChange={(v) => list.setFilter('q', v)} />
        <FilterSelect label="Status" value={search.status} onChange={(v) => list.setFilter('status', v)} options={STATUS_OPTIONS} />
        <FilterSelect label="Rolle" value={search.role} onChange={(v) => list.setFilter('role', v)} options={roleOptions} />
        <FilterSelect label="Zugang" value={search.access} onChange={(v) => list.setFilter('access', v)} options={ACCESS_OPTIONS} />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Benutzer"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          sorting={list.sorting}
          onSortingChange={list.onSortingChange}
          emptyTitle="Keine Benutzer gefunden"
        />
      )}
      <UserFormSheet
        user={null}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(user) => navigate({ to: '/administration/benutzer/$userId', params: { userId: String(user.id) } })}
      />
    </>
  );
}

function userColumns() {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <Link to="/administration/benutzer/$userId" params={{ userId: String(row.original.id) }} className="font-medium text-primary hover:underline">
          {row.original.name}
        </Link>
      ),
    },
    { accessorKey: 'email', header: 'E-Mail', cell: ({ row }) => <span className="break-all">{row.original.email}</span> },
    {
      id: 'roles',
      header: 'Rollen',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="flex flex-wrap gap-1">
          {(row.original.roles ?? []).map((r) => (
            <Badge key={r} variant="secondary">
              {ROLE_LABELS[r] ?? r}
            </Badge>
          ))}
        </span>
      ),
    },
    { id: 'status', header: 'Status', enableSorting: false, cell: ({ row }) => <StatusBadge domain="user" status={row.original.status} /> },
    {
      id: 'two_factor',
      header: '2FA',
      enableSorting: false,
      cell: ({ row }) => (row.original.two_factor_enabled ? <Badge variant="success">Aktiv</Badge> : <Badge variant="neutral">Aus</Badge>),
    },
    {
      accessorKey: 'last_login_at',
      header: 'Letzte Anmeldung',
      cell: ({ row }) => <span className="numeric">{row.original.last_login_at ? formatDateTime(row.original.last_login_at) : 'nie'}</span>,
    },
    {
      accessorKey: 'access_expires_at',
      header: 'Zugang bis',
      cell: ({ row }) => <AccessExpiry user={row.original} />,
    },
  ];
}
